import { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Space, Typography, message, Spin } from 'antd';
import { SaveOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, PlusOutlined, MinusCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { contactAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function ContactPage() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [dataId, setDataId] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await contactAPI.get();
            if (response.data) {
                form.setFieldsValue(response.data);
                setDataId(response.data.id || response.data._id);
            }
        } catch (error) {
            console.error('Error fetching contact data:', error);
            message.error('Failed to fetch contact data');
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            // Clean empty strings from arrays
            const cleanData = {
                ...values,
                phone: values.phone?.filter(p => p?.trim()) || [],
                email: values.email?.filter(e => e?.trim()) || [],
            };

            if (dataId) {
                await contactAPI.update(dataId, cleanData);
                message.success('Contact information updated successfully!');
            } else {
                const response = await contactAPI.create(cleanData);
                setDataId(response.data.id || response.data._id);
                message.success('Contact information created successfully!');
            }
            fetchData();
        } catch (error) {
            message.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}><PhoneOutlined /> Contact Information</Title>
                <Text type="secondary">Manage contact details and office information</Text>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ phone: [''], email: [''] }}>
                {/* Address */}
                <Card title="📍 Office Address" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.Item name="address" label="Full Address" rules={[{ required: true }]}>
                        <TextArea rows={4} prefix={<EnvironmentOutlined />} placeholder="Jl. Example Street No. 123, Jakarta..." />
                    </Form.Item>
                    <Form.Item name="mapUrl" label="Google Maps Embed URL">
                        <Input size="large" placeholder="https://www.google.com/maps/embed?..." />
                    </Form.Item>
                </Card>

                {/* Phone Numbers */}
                <Card title="📞 Phone Numbers" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.List name="phone">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                        <Form.Item {...field} label={index === 0 ? 'Phone Number' : ''} style={{ marginBottom: 0, flex: 1 }}>
                                            <Input size="large" prefix={<PhoneOutlined />} placeholder="+62 21 1234 5678" />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)}>Remove</Button>
                                        )}
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                                    Add Phone Number
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Email Addresses */}
                <Card title="📧 Email Addresses" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.List name="email">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                        <Form.Item {...field} label={index === 0 ? 'Email Address' : ''} style={{ marginBottom: 0, flex: 1 }} rules={[{ type: 'email' }]}>
                                            <Input size="large" prefix={<MailOutlined />} placeholder="info@company.com" />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)}>Remove</Button>
                                        )}
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                                    Add Email Address
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Working Hours */}
                <Card title="🕐 Working Hours" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.Item name="workingHours" label="Office Hours">
                        <TextArea rows={3} prefix={<ClockCircleOutlined />} placeholder="Monday - Friday: 08:00 - 17:00&#10;Saturday: 08:00 - 12:00" />
                    </Form.Item>
                </Card>

                {/* Submit Button */}
                <Form.Item style={{ marginTop: 32 }}>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                        block
                        style={{
                            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                            border: 'none',
                            height: 48
                        }}
                    >
                        Save Contact Information
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
